import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Plus, Pencil, Crown, Power, Loader2 } from "lucide-react";

const EMPTY = { id: "", name: "", username: "", password: "" };

const OwnerRow = ({ o, me, onEdit, onToggle }) => {
  const canEdit = !o.is_primary || o.id === me;
  const canToggle = !o.is_primary && o.id !== me;
  return (
    <div data-testid={`owner-row-${o.username}`} className={`flex items-center justify-between rounded-xl px-4 py-3 ${o.active ? "bg-neutral-100" : "bg-neutral-100/50 opacity-60"}`}>
      <div>
        <div className="flex flex-wrap items-center gap-1.5 font-semibold text-neutral-900">
          {o.name}
          {o.is_primary && <span data-testid={`owner-primary-badge-${o.username}`} className="flex items-center gap-1 rounded-full bg-gold/15 px-2 py-0.5 text-[10px] font-bold text-gold"><Crown size={11} /> Owner Utama</span>}
          {o.id === me && <span className="rounded-full bg-neutral-200 px-2 py-0.5 text-[10px] font-bold text-neutral-600">Anda</span>}
          <span data-testid={`owner-status-${o.username}`} className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${o.active ? "bg-emerald-100 text-emerald-700" : "bg-crimson/15 text-crimson"}`}>{o.active ? "Aktif" : "Nonaktif"}</span>
        </div>
        <div className="text-xs text-muted-foreground">@{o.username}</div>
      </div>
      <div className="flex gap-1">
        {canEdit && <Button data-testid={`owner-edit-${o.username}`} onClick={() => onEdit(o)} variant="ghost" size="icon" className="text-muted-foreground hover:text-gold"><Pencil size={16} /></Button>}
        {canToggle && (
          <Button data-testid={`owner-toggle-${o.username}`} onClick={() => onToggle(o)} variant="outline" size="sm"
            className={o.active ? "border-crimson/40 text-crimson hover:bg-crimson/10" : "border-emerald-500/40 text-emerald-700 hover:bg-emerald-50"}>
            <Power size={14} className="mr-1" /> {o.active ? "Nonaktifkan" : "Aktifkan"}
          </Button>
        )}
      </div>
    </div>
  );
};

const OwnerForm = ({ form, setForm, busy, onSave, onClose }) => (
  <Dialog open={!!form} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="glass border-neutral-200 text-neutral-900" data-testid="owner-form-dialog">
      <DialogHeader>
        <DialogTitle className="font-display">{form?.id ? "Ubah Akun Owner" : "Tambah Akun Owner"}</DialogTitle>
        <DialogDescription>Akun owner memiliki akses penuh seperti Owner Utama.</DialogDescription>
      </DialogHeader>
      {form && (
        <form onSubmit={onSave} className="space-y-3">
          <div><Label className="text-xs">Nama</Label>
            <Input data-testid="owner-form-name" value={form.name} required onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
          <div><Label className="text-xs">Username</Label>
            <Input data-testid="owner-form-username" value={form.username} required autoComplete="off" onChange={(e) => setForm({ ...form, username: e.target.value })} className="mt-1" /></div>
          <div><Label className="text-xs">{form.id ? "Password Baru (kosongkan jika tidak diubah)" : "Password (min. 6 karakter)"}</Label>
            <Input data-testid="owner-form-password" type="password" value={form.password} required={!form.id} minLength={6} autoComplete="new-password" onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1" /></div>
          <Button data-testid="owner-form-submit" disabled={busy} className="w-full bg-crimson font-bold hover:bg-red-500">
            {busy ? <Loader2 className="animate-spin" /> : "Simpan"}
          </Button>
        </form>
      )}
    </DialogContent>
  </Dialog>
);

export const OwnerAccountsPanel = () => {
  const { user, reload } = useAuth();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/owners"); setItems(data); } catch (e) { toast.error(apiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const body = { name: form.name, username: form.username, password: form.password };
      if (form.id) await api.put(`/owners/${form.id}`, body); else await api.post("/owners", body);
      toast.success(form.id ? "Akun owner diperbarui" : "Akun owner dibuat");
      if (form.id === user?.id) reload();
      setForm(null); load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const toggle = async (o) => {
    if (o.active && !window.confirm(`Nonaktifkan akun ${o.name}? Akun ini tidak akan bisa login.`)) return;
    try { await api.post(`/owners/${o.id}/status`, { active: !o.active }); toast.success(o.active ? "Akun dinonaktifkan" : "Akun diaktifkan"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <Card className="card-surface rounded-2xl p-5" data-testid="owner-accounts-panel">
      <div className="mb-4 flex items-center justify-between">
        <div className="font-display text-lg font-bold text-neutral-900">Akun Owner</div>
        <Button data-testid="add-owner-btn" onClick={() => setForm({ ...EMPTY })} className="bg-crimson font-bold hover:bg-red-500"><Plus size={16} className="mr-1" /> Tambah Owner</Button>
      </div>
      <div className="space-y-2">
        {items.map((o) => (
          <OwnerRow key={o.id} o={o} me={user?.id} onToggle={toggle}
            onEdit={(x) => setForm({ id: x.id, name: x.name, username: x.username, password: "" })} />
        ))}
      </div>
      <OwnerForm form={form} setForm={setForm} busy={busy} onSave={save} onClose={() => setForm(null)} />
    </Card>
  );
};
